import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { BranchSelect, BranchTag, useBranches } from "@/components/BranchBits";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" });
}

function HolidayForm({ open, onOpenChange, onSaved }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [branchId, setBranchId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setTitle(""); setDate(""); setBranchId(""); } }, [open]);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!date) return toast.error("Date is required");
    setSaving(true);
    try {
      await api.post("/holidays", { title: title.trim(), date, branch_id: branchId || null });
      toast.success("Holiday added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add holiday"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="holiday-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Holiday</DialogTitle>
          <DialogDescription>Mark a company-wide non-working day.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title *</Label>
            <Input data-testid="holiday-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Diwali" />
          </div>
          <div>
            <Label>Date *</Label>
            <Input data-testid="holiday-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <BranchSelect value={branchId} onChange={setBranchId} label="Applies to" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="holiday-submit" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Add holiday"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Holidays() {
  const { user } = useAuth();
  const canCreate = user?.role === "admin" || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/holidays").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try { await api.delete(`/holidays/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const columns = [
    { key: "title", label: "Title", sortable: true, render: (r) => <span className="font-medium">{r.title}</span> },
    { key: "date", label: "Date", sortable: true, render: (r) => <span className="number">{fmtDate(r.date)}</span> },
    { key: "branch_id", label: "Applies to", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "day", label: "Day", render: (r) => {
      const d = new Date(`${r.date}T00:00:00`);
      const wd = isNaN(d) ? "—" : d.toLocaleDateString(undefined, { weekday: "long" });
      const isWeekend = !isNaN(d) && (d.getDay() === 0 || d.getDay() === 6);
      return <Badge variant={isWeekend ? "secondary" : "outline"} className="text-[10px]">{wd}</Badge>;
    } },
  ];

  const rowActions = (r) => r.can_modify ? (
    <div className="flex justify-end gap-1">
      <Button variant="ghost" size="icon" data-testid={`holiday-del-${r.id}`} onClick={() => del(r.id)}>
        <Trash2 className="w-4 h-4 text-red-500" />
      </Button>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        testid="holidays-header"
        title="Holidays"
        subtitle={`${rows.length} company holiday${rows.length === 1 ? "" : "s"}`}
        actions={canCreate && (
          <Button data-testid="new-holiday-btn" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />Add Holiday
          </Button>
        )}
      />
      <DataTable
        testid="holidays-table"
        columns={columns}
        data={rows}
        loading={loading}
        empty="No holidays yet. Add one to mark it as a company-wide off day."
        searchKeys={["title", "date"]}
        rowActions={rowActions}
        initialPageSize={10}
      />
      <HolidayForm open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
